import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Leaf, Sparkles, Heart, Clock, Users } from 'lucide-react';
import { businessInfo, faqs } from '../data/mock';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

const NutritionalTherapyPage = () => {
  const nutritionFaqs = faqs.filter(faq => 
    faq.question.toLowerCase().includes('nutrition') || 
    faq.question.toLowerCase().includes('consultation') ||
    faq.question.toLowerCase().includes('expect')
  );

  const benefits = [
    "Personalized dietary recommendations based on your unique needs",
    "Identification of nutritional deficiencies and imbalances",
    "Custom supplement protocols with high-quality products",
    "Support for digestive health and nutrient absorption",
    "Guidance on whole food nutrition and meal planning",
    "Ongoing support and protocol adjustments as needed"
  ];

  const whoItsFor = [
    "Those seeking natural approaches to health concerns",
    "Individuals with chronic fatigue or low energy",
    "People with digestive issues or food sensitivities",
    "Anyone wanting to optimize their overall wellness",
    "Those looking to support healthy weight management",
    "Individuals recovering from illness or seeking prevention"
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-[#6E9EB9]/10 via-white to-[#F7F8FA] overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-20 right-10 w-64 h-64 bg-[#6E9EB9]/5 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-10 w-48 h-48 bg-[#AF9EB8]/10 rounded-full blur-3xl" />
        
        <div className="container-custom relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-[#6E9EB9]/10 rounded-full mb-6">
              <Leaf size={16} className="text-[#6E9EB9]" />
              <span className="text-sm font-medium text-[#6E9EB9]">Primary Service</span>
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-medium text-[#2C2F36] mb-6">
              Nutritional Therapy
            </h1>
            <p className="text-xl text-[#4A5568] mb-8 max-w-3xl mx-auto">
              The Answer to Chronic Illness. Our personalized nutritional therapy protocols 
              are designed to nourish your body and support your innate healing abilities.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="btn-primary">
                Schedule Consultation
                <ArrowRight size={18} />
              </Link>
              <Link to="/orientation" className="btn-secondary">
                Attend Free Orientation
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* What is Nutritional Therapy */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block px-4 py-1 bg-[#AF9EB8]/10 rounded-full text-sm font-medium text-[#AF9EB8] mb-4">
                What We Do
              </span>
              <h2 className="text-3xl md:text-4xl font-serif font-medium text-[#2C2F36] mb-6">
                What is Nutritional Therapy?
              </h2>
              <p className="text-[#4A5568] mb-6 leading-relaxed">
                Nutritional therapy is a holistic approach to health that uses food and targeted 
                supplementation as tools for healing. We believe that the body has an incredible 
                capacity to heal itself when given the proper nutritional support.
              </p>
              <p className="text-[#4A5568] mb-6 leading-relaxed">
                Our approach goes beyond simply addressing symptoms. We work to identify the 
                underlying nutritional imbalances and deficiencies that may be contributing to 
                your health concerns, then develop a personalized protocol to restore balance.
              </p>
              <p className="text-[#4A5568] leading-relaxed">
                At Serenity Natural Health & Wellness, nutritional therapy is the foundation 
                of our practice. We combine dietary strategies with herbal remedies, tinctures, 
                and high-quality supplements to support immune function and vitality.
              </p>
            </div>
            <div className="bg-gradient-to-br from-[#6E9EB9]/10 to-[#AF9EB8]/10 rounded-2xl p-8 md:p-10">
              <h3 className="font-serif text-2xl font-medium text-[#2C2F36] mb-6">
                Our Nutritional Approach Includes:
              </h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-4">
                  <div className="p-2 bg-white rounded-full shadow-sm flex-shrink-0">
                    <Leaf size={20} className="text-[#6E9EB9]" />
                  </div>
                  <div>
                    <h4 className="font-medium text-[#2C2F36]">Whole Food Nutrition</h4>
                    <p className="text-sm text-[#4A5568]">Emphasis on nutrient-dense, real foods</p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <div className="p-2 bg-white rounded-full shadow-sm flex-shrink-0">
                    <Sparkles size={20} className="text-[#AF9EB8]" />
                  </div>
                  <div>
                    <h4 className="font-medium text-[#2C2F36]">Targeted Supplementation</h4>
                    <p className="text-sm text-[#4A5568]">High-quality supplements for specific needs</p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <div className="p-2 bg-white rounded-full shadow-sm flex-shrink-0">
                    <Heart size={20} className="text-[#6E9EB9]" />
                  </div>
                  <div>
                    <h4 className="font-medium text-[#2C2F36]">Herbal Remedies</h4>
                    <p className="text-sm text-[#4A5568]">Natural herbs and tinctures for support</p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <div className="p-2 bg-white rounded-full shadow-sm flex-shrink-0">
                    <Users size={20} className="text-[#AF9EB8]" />
                  </div>
                  <div>
                    <h4 className="font-medium text-[#2C2F36]">Personalized Protocols</h4>
                    <p className="text-sm text-[#4A5568]">Plans tailored to your unique needs</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* What to Expect */}
      <section className="section-padding bg-[#F7F8FA]">
        <div className="container-custom">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1 bg-[#6E9EB9]/10 rounded-full text-sm font-medium text-[#6E9EB9] mb-4">
              Your Visit
            </span>
            <h2 className="text-3xl md:text-4xl font-serif font-medium text-[#2C2F36] mb-4">
              What to Expect at Your Consultation
            </h2>
            <div className="floral-divider w-24 mx-auto mb-6" />
            <p className="text-[#4A5568] max-w-2xl mx-auto">
              Your nutritional therapy journey begins with a comprehensive consultation 
              where we get to know you and your health goals.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-2xl shadow-sm">
              <div className="w-12 h-12 bg-[#6E9EB9]/10 rounded-full flex items-center justify-center mb-6">
                <span className="font-serif text-xl font-bold text-[#6E9EB9]">1</span>
              </div>
              <h3 className="font-serif text-xl font-medium text-[#2C2F36] mb-4">
                Health Assessment
              </h3>
              <p className="text-[#4A5568]">
                We'll conduct a thorough review of your health history, current concerns, 
                medications, supplements, and lifestyle factors.
              </p>
            </div>
            
            <div className="bg-white p-8 rounded-2xl shadow-sm">
              <div className="w-12 h-12 bg-[#AF9EB8]/10 rounded-full flex items-center justify-center mb-6">
                <span className="font-serif text-xl font-bold text-[#AF9EB8]">2</span>
              </div>
              <h3 className="font-serif text-xl font-medium text-[#2C2F36] mb-4">
                Protocol Development
              </h3>
              <p className="text-[#4A5568]">
                Based on your assessment, Julie will develop a personalized nutritional 
                protocol including dietary recommendations and supplementation.
              </p>
            </div>
            
            <div className="bg-white p-8 rounded-2xl shadow-sm">
              <div className="w-12 h-12 bg-[#93A8C7]/10 rounded-full flex items-center justify-center mb-6">
                <span className="font-serif text-xl font-bold text-[#93A8C7]">3</span>
              </div>
              <h3 className="font-serif text-xl font-medium text-[#2C2F36] mb-4">
                Ongoing Support
              </h3>
              <p className="text-[#4A5568]">
                We provide continued guidance and support, adjusting your protocol 
                as needed to ensure optimal progress on your wellness journey.
              </p>
            </div>
          </div>

          <div className="mt-12 bg-white rounded-2xl p-8 border border-[#6E9EB9]/20">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-[#6E9EB9]/10 rounded-full flex-shrink-0">
                <Clock size={24} className="text-[#6E9EB9]" />
              </div>
              <div>
                <h4 className="font-serif text-lg font-medium text-[#2C2F36] mb-2">
                  What to Bring to Your First Appointment
                </h4>
                <ul className="text-[#4A5568] space-y-2">
                  <li>• List of current medications and supplements</li>
                  <li>• Any relevant health records or test results</li>
                  <li>• A food diary if possible (past 3-7 days)</li>
                  <li>• Your health goals and questions</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits & Who It's For */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Benefits */}
            <div>
              <span className="inline-block px-4 py-1 bg-[#6E9EB9]/10 rounded-full text-sm font-medium text-[#6E9EB9] mb-4">
                Benefits
              </span>
              <h2 className="text-3xl font-serif font-medium text-[#2C2F36] mb-6">
                How Nutritional Therapy Can Support You
              </h2>
              <ul className="space-y-4">
                {benefits.map((benefit, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="p-1 bg-[#6E9EB9]/10 rounded-full mt-0.5 flex-shrink-0">
                      <Check size={16} className="text-[#6E9EB9]" />
                    </div>
                    <span className="text-[#4A5568]">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Who It's For */}
            <div>
              <span className="inline-block px-4 py-1 bg-[#AF9EB8]/10 rounded-full text-sm font-medium text-[#AF9EB8] mb-4">
                Who It's For
              </span>
              <h2 className="text-3xl font-serif font-medium text-[#2C2F36] mb-6">
                Is Nutritional Therapy Right for You?
              </h2>
              <ul className="space-y-4">
                {whoItsFor.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="p-1 bg-[#AF9EB8]/10 rounded-full mt-0.5 flex-shrink-0">
                      <Check size={16} className="text-[#AF9EB8]" />
                    </div>
                    <span className="text-[#4A5568]">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-padding bg-[#F7F8FA]">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-1 bg-[#6E9EB9]/10 rounded-full text-sm font-medium text-[#6E9EB9] mb-4">
                FAQ
              </span>
              <h2 className="text-3xl md:text-4xl font-serif font-medium text-[#2C2F36] mb-4">
                Questions About Nutritional Therapy
              </h2>
              <div className="floral-divider w-24 mx-auto" />
            </div>

            <Accordion type="single" collapsible className="space-y-4">
              {faqs.slice(0, 4).map((faq) => (
                <AccordionItem 
                  key={faq.id} 
                  value={`faq-${faq.id}`}
                  className="bg-white rounded-lg px-6 border-none shadow-sm"
                >
                  <AccordionTrigger className="text-left font-medium text-[#2C2F36] hover:text-[#6E9EB9] hover:no-underline py-4">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-[#4A5568] pb-4">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#6E9EB9] to-[#93A8C7] text-white">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-serif font-medium mb-6">
            Begin Your Nutritional Therapy Journey
          </h2>
          <p className="text-white/90 max-w-2xl mx-auto mb-8">
            Take the first step towards optimal health. Schedule your personalized 
            nutritional therapy consultation with {businessInfo.owner.name} today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/contact" 
              className="px-8 py-4 bg-white text-[#6E9EB9] rounded-full font-semibold hover:bg-white/90 transition-colors inline-flex items-center justify-center gap-2"
            >
              Book Consultation
              <ArrowRight size={18} />
            </Link>
            <Link 
              to="/orientation" 
              className="px-8 py-4 bg-transparent border-2 border-white text-white rounded-full font-semibold hover:bg-white/10 transition-colors inline-flex items-center justify-center gap-2"
            >
              Free Orientation Class
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default NutritionalTherapyPage;